import React, { memo, useState } from 'react'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { ProgressSpinner } from 'primereact/progressspinner'

import useText from '../../../../../common/hooks/useText'
import { PaymentAdministrativeFeeDisplaySummary } from '../../../../../common/redux/api/web-api'
import { Dialog } from '../../../../../common/uikit/Dialog'
import Text from '../../../../../ui/Text'

import { InvoiceDetailsForm } from './invoice-details-form'
import type { InvoicePaymentDetails } from './invoice-details-form-model'

export type BillingPaymentSetupDialogProps = {
    visible: boolean
    onHide: () => void
    onSubmitInvoice: (data: InvoicePaymentDetails) => void | Promise<void>
    onSubmitCard: (type: 'individual' | 'organization') => void | Promise<void>
    productId: string
    priceId: string
    isCreatingSubscription?: boolean
    administrativeFee?: PaymentAdministrativeFeeDisplaySummary | null
    currency: string
    isLoadingCheckoutSession: boolean
    isCardPaymentDisabled?: boolean
    isInvoiceDiscountCodeEnabled?: boolean
    discountCodeQuantity?: number
}

type PaymentSetupStep = 'customer-type' | 'business-payment-method' | 'invoice-details'

type PaymentSetupStepConfig = {
    headerKey: string
    previousStep?: PaymentSetupStep
}

const STEP_CONFIG: Record<PaymentSetupStep, PaymentSetupStepConfig> = {
    'customer-type': { headerKey: 'lblBeforeYouContinue' },
    'business-payment-method': {
        headerKey: 'lblChoosePaymentMethod',
        previousStep: 'customer-type',
    },
    'invoice-details': {
        headerKey: 'lblInvoiceDetails',
        previousStep: 'business-payment-method',
    },
}

export const BillingPaymentSetupDialog = memo(function BillingPaymentSetupDialog({
    visible,
    onHide,
    onSubmitInvoice,
    onSubmitCard,
    productId,
    priceId,
    isCreatingSubscription,
    administrativeFee,
    currency,
    isLoadingCheckoutSession,
    isCardPaymentDisabled = false,
    isInvoiceDiscountCodeEnabled = false,
    discountCodeQuantity,
}: BillingPaymentSetupDialogProps): React.ReactElement {
    const text = useText()
    const [step, setStep] = useState<PaymentSetupStep>('customer-type')
    const { headerKey, previousStep } = STEP_CONFIG[step]
    const isSpinnerVisible = isLoadingCheckoutSession && step !== 'invoice-details'
    const cardPaymentUnavailableReason = isCardPaymentDisabled ? (
        <p className="m-0 mt-2 text-sm text-600 text-center">{text('lblSubscriptionRequiresBillableConnectors')}</p>
    ) : null

    const footer = !isSpinnerVisible ? (
        <div className="flex justify-content-end gap-2">
            <Button type="button" label={text('btnCancel')} text onClick={onHide} />
            {previousStep ? (
                <Button type="button" label={text('btnBack')} outlined onClick={() => setStep(previousStep)} />
            ) : null}
        </div>
    ) : null

    const renderContent = () => {
        switch (step) {
            case 'customer-type':
                return (
                    <div className="flex flex-column justify-content-center align-items-center">
                        {!isLoadingCheckoutSession ? (
                            <>
                                <p>
                                    <Text code="lblPaymentModalDescription" />
                                </p>

                                <Button
                                    className="w-full"
                                    label={text('lblCompany')}
                                    onClick={() => setStep('business-payment-method')}
                                />
                                <Divider>{text('lblOr').toLowerCase()}</Divider>
                                <Button
                                    label={text('lblIndividual')}
                                    className="w-full"
                                    disabled={isCardPaymentDisabled}
                                    onClick={() => onSubmitCard('individual')}
                                />
                                {cardPaymentUnavailableReason}
                            </>
                        ) : (
                            <ProgressSpinner />
                        )}
                    </div>
                )

            case 'business-payment-method':
                if (isLoadingCheckoutSession) return <ProgressSpinner />

                return (
                    <div className="flex justify-content-center flex-column">
                        <Button
                            label={text('lblPayByCard')}
                            className="w-full"
                            disabled={isCardPaymentDisabled}
                            onClick={() => onSubmitCard('organization')}
                        />
                        {cardPaymentUnavailableReason}

                        <Divider>{text('lblOr').toLowerCase()}</Divider>

                        <Button
                            label={text('lblPayByEmailInvoice')}
                            className="w-full"
                            onClick={() => setStep('invoice-details')}
                        />
                    </div>
                )

            case 'invoice-details':
                return (
                    <InvoiceDetailsForm
                        onSubmit={onSubmitInvoice}
                        administrativeFee={administrativeFee}
                        currency={currency}
                        isCreatingSubscription={isCreatingSubscription}
                        isDiscountCodeEnabled={isInvoiceDiscountCodeEnabled}
                        discountCodeInfo={{
                            productId,
                            productPriceVersionId: priceId,
                            quantity: discountCodeQuantity,
                        }}
                    />
                )
        }
    }

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={text(headerKey)}
            footer={footer}
            data-testid="billing-payment-setup-dialog"
        >
            {renderContent()}
        </Dialog>
    )
})
